import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, IconButton, Popover, Stack, Typography } from '@mui/material';
import { ArrowUpward, Close } from '@mui/icons-material';
import classes from '../docFwb.module.css';

const AlertPromptModal = ({
  open = false,
  anchorEl = null,
  onClose,
  initialPrompt = '',
  onSubmit,
  isSubmitting = false,
  errorMessage = null,
}) => {
  const [promptText, setPromptText] = useState(initialPrompt || '');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPromptText(initialPrompt || '');
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.selectionStart = textareaRef.current.value.length;
          textareaRef.current.selectionEnd = textareaRef.current.value.length;
        }
      }, 100);
    }
  }, [open, initialPrompt]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    const trimmed = (promptText || '').trim();
    if (!trimmed || isSubmitting) return;
    if (onSubmit) {
      onSubmit(trimmed);
    }
  };

  const isSendDisabled = !promptText.trim() || isSubmitting;

  return (
    <Popover
      sx={{ zIndex: 1400 }}
      open={Boolean(open && anchorEl)}
      anchorEl={anchorEl}
      onClose={isSubmitting ? undefined : onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          className: classes.savePopoverPaper,
          sx: {
            p: 2,
            width: 380,
            maxWidth: '92vw',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(9, 43, 74, 0.16)',
            border: '1px solid #e2e8f0',
          },
        },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1.2}>
          <Typography variant='subtitle2' fontWeight={700} color='var(--primaryblue)' sx={{ fontSize: '13px' }}>
            Fix Alerts with CABA AI
          </Typography>
          <IconButton
            size='small'
            onClick={isSubmitting ? undefined : onClose}
            disabled={isSubmitting}
            sx={{
              p: 0.5,
              color: '#94a3b8',
            }}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>

        <Stack direction='column' spacing={1.5}>
          <Typography
            variant='caption'
            color='#64748b'
            sx={{
              fontSize: '11.5px',
              lineHeight: 1.4,
              display: 'block',
            }}
          >
            Review or edit the combined prompt generated from your selected alerts:
          </Typography>

          {errorMessage && (
            <Box
              sx={{
                p: 1.25,
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#b91c1c',
                fontSize: '11.5px',
              }}
            >
              {errorMessage}
            </Box>
          )}

          <div className={classes.promptModalInputWrapper}>
            <textarea
              ref={textareaRef}
              className={classes.promptModalTextarea}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='Enter prompt for CABA AI...'
              rows={4}
              disabled={isSubmitting}
            />
            <div className={classes.promptModalFooter}>
              <Typography variant='caption' color='text.secondary' fontSize='11px'>
                Press <strong>Enter</strong> to send, <strong>Shift + Enter</strong> for a new line
              </Typography>
              <button
                type='button'
                className={classes.chatSendButton}
                onClick={handleSend}
                disabled={isSendDisabled}
                title='Send prompt to CABA AI'
              >
                {isSubmitting ? (
                  <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                ) : (
                  <ArrowUpward sx={{ fontSize: 18 }} />
                )}
              </button>
            </div>
          </div>
        </Stack>
      </Box>
    </Popover>
  );
};

export default AlertPromptModal;

